import { useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { submitFreeWeekLead } from "@/services/leadService";
import { trackEvent } from "@/services/analytics";
import { useLeadModal } from "@/components/site/SiteContext";
import { school } from "@/data/schoolData";
import { storeLead } from "@/services/bookingService";

const initialForm = { name: "", email: "", smsConsent: false, phone: "" };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SMS_CONSENT_VERSION = "sms-consent-v1";
const smsConsentText = (schoolName) => `I agree to receive promotional texts from ${schoolName}. Msg & data rates may apply. Msg freq varies. STOP to opt out, HELP for help.`;

export function FreeWeekModal() {
  const { isOpen, closeLeadModal } = useLeadModal();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const firstInputRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setForm(initialForm);
    setError("");
    firstInputRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeLeadModal();
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll("button, input, a[href], select, textarea, [tabindex]:not([tabindex='-1'])");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("modal-open");
    return () => { document.removeEventListener("keydown", onKeyDown); document.body.classList.remove("modal-open"); };
  }, [isOpen, closeLeadModal]);

  if (!isOpen) return null;

  const update = (field) => (event) => {
    setError("");
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    if (field === "name" && value.length > 1) trackEvent("free_week_form_started");
  };

  const handleInvalid = (event) => {
    event.preventDefault();
    setError("Please complete your name, email, phone, and text consent.");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.email.trim() && !EMAIL_PATTERN.test(form.email.trim())) { setError("Please enter a valid email address."); return; }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.smsConsent) { setError("Please complete your name, email, phone, and text consent."); return; }
    try {
      const lead = await submitFreeWeekLead({
        ...form,
        smsConsentText: smsConsentText(school.name),
        smsConsentVersion: SMS_CONSENT_VERSION,
        smsConsentSource: window.location.pathname || "/",
      });
      storeLead(lead);
      trackEvent("free_week_form_submitted", { mode: "demo-backend" });
      closeLeadModal();
      navigate("/schedule", { state: { freeWeekRequested: true } });
    } catch (requestError) {
      setError(requestError.message.toLowerCase().includes("email") ? "Please enter a valid email address." : "We could not prepare your Free Week Pass. Please try again.");
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeLeadModal()} data-testid="free-week-modal-backdrop">
      <div className="lead-modal" role="dialog" aria-modal="true" aria-labelledby="free-week-modal-title" ref={dialogRef} data-testid="free-week-modal">
        <button type="button" className="modal-close" onClick={closeLeadModal} aria-label="Close Free Week form" data-testid="free-week-modal-close"><X size={20} aria-hidden="true" /></button>
        <h2 id="free-week-modal-title" data-testid="free-week-modal-title">Where should I send your Free Week Pass?</h2>
        <form onSubmit={submit} onInvalid={handleInvalid} noValidate data-testid="free-week-lead-form">
          <label htmlFor="lead-name">Name</label>
          <input id="lead-name" ref={firstInputRef} type="text" value={form.name} onChange={update("name")} autoComplete="name" required data-testid="free-week-name-input" />
          <label htmlFor="lead-email">Email</label>
          <input id="lead-email" type="email" value={form.email} onChange={update("email")} autoComplete="email" required aria-invalid={Boolean(form.email.trim() && !EMAIL_PATTERN.test(form.email.trim()))} aria-describedby="lead-email-error" data-testid="free-week-email-input" />
          {form.email.trim() && !EMAIL_PATTERN.test(form.email.trim()) && <p id="lead-email-error" className="form-error" role="alert" data-testid="free-week-email-error">Please enter a valid email address.</p>}
          <label htmlFor="lead-phone">Phone</label>
          <input id="lead-phone" type="tel" inputMode="tel" value={form.phone} onChange={update("phone")} autoComplete="tel" required data-testid="free-week-phone-input" />
          <div className="lead-modal-consent">
            <input id="lead-sms-consent" type="checkbox" checked={form.smsConsent} onChange={update("smsConsent")} required data-testid="free-week-sms-consent-checkbox" />
            <label htmlFor="lead-sms-consent" data-testid="free-week-sms-consent-label">{smsConsentText(school.name)}</label>
          </div>
          {error && <p className="form-error" role="alert" data-testid="free-week-form-error">{error}</p>}
          <button type="submit" className="button button-dark modal-submit" data-testid="free-week-submit-button">Send My Free Week Pass <ArrowRight size={16} aria-hidden="true" /></button>
          <div className="lead-modal-legal" data-testid="free-week-legal-links"><a href="/terms" target="_blank" rel="noreferrer" data-testid="free-week-terms-link">Terms</a><span aria-hidden="true">|</span><a href="/privacy" target="_blank" rel="noreferrer" data-testid="free-week-privacy-link">Privacy</a></div>
        </form>
        <small>🔒 Your information is 100% safe.</small>
      </div>
    </div>
  );
}
